import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";

/** Admin : prépare le compte Express d'un gérant et obtient / envoie le lien d'activation Stripe. */
export function OnboardDialog({ target, onClose, onDone }) {
  const [notify, setNotify] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (target) { setResult(null); setNote(""); setNotify(true); } }, [target]);

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/stripe-connect/${target.id}/onboard`, { notify, note: note.trim() });
      setResult(data);
      toast.success(data.notified ? "Compte préparé — lien envoyé au gérant" : "Compte préparé — lien généré");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sc-onboard-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">Connecter {target?.name}</DialogTitle>
          <DialogDescription>Un compte Stripe Express est créé au nom de l'entreprise ({target?.code}). Le gérant complète ensuite son dossier (identité, IBAN) via un lien personnel : les paiements de son mini-site lui sont versés directement.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="sc-onboard-notify" />Envoyer le lien au gérant (email + notification + push)</label>
            <div><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Message personnel joint à l'email (facultatif)" maxLength={300} data-testid="sc-onboard-note" className="min-h-[70px]" /></div>
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="sc-onboard-result">
            <div className="text-xs text-[#495057]">Compte <span className="font-mono">{result.account_id}</span>{result.notified ? " · lien envoyé au gérant" : " · non envoyé"}</div>
            <div className="flex gap-2 items-center border border-[#DEE2E6] bg-[#F8F9FA] p-2">
              <code className="text-[11px] flex-1 truncate" data-testid="sc-onboard-url">{result.url}</code>
              <Button size="sm" variant="outline" onClick={() => { copyText(result.url); toast.success("Lien copié"); }} data-testid="sc-onboard-copy" className="h-8"><Copy className="h-3 w-3" /></Button>
              <a href={result.url} target="_blank" rel="noreferrer" data-testid="sc-onboard-open" className="inline-flex items-center h-8 px-2 border border-[#0A0A0A] text-[10px] font-semibold"><ExternalLink className="h-3 w-3" /></a>
            </div>
            <div className="text-[11px] text-[#868E96]">Lien à usage unique et à durée limitée : Stripe le régénère automatiquement en cas d'expiration.</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sc-onboard-close">{result ? "Fermer" : "Annuler"}</Button>
          {!result && <Button onClick={go} disabled={busy} data-testid="sc-onboard-go" className="bg-[#635BFF] hover:bg-[#4f46e5] text-white">{busy ? "…" : "Préparer le compte & générer le lien"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
